import {
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Input,
  InputAdornment,
} from "@mui/material";
import _ from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { useStoreState, useStoreActions } from "../../models/RootStore";
import { t } from "ttag";
import { EditIcon } from "../../iconImages";
import { Delete } from "../../icons";
import ServiceNavigationBar from "../../widgets/Menu/ServiceNavigationBar";
import useStyles from "../DebugDevices/DebugDevices.style";
import { Close, Search } from "@mui/icons-material";
import AddEditBlockedDevice from "./AddEditBlockedDevice";

const BlockedDevices = (props: any) => {
  const styles: any = useStyles();
  const allBlockedDevices = useStoreState((state) => state.devices.allBlockedDevices);
  const getAllBlockedDevices = useStoreActions((actions) => actions.devices.getAllBlockedDevices);
  const createBlockedDevice = useStoreActions((actions) => actions.devices.createBlockedDevice);
  const editBlockedDevice = useStoreActions((actions) => actions.devices.editBlockedDevice);
  const deleteBlockedDevice = useStoreActions((actions) => actions.devices.deleteBlockedDevice);
  const addMessage = useStoreActions((actions) => actions.errorMessage.addMessage);

  useEffect(() => {
    getAllBlockedDevices();
  }, []);

  const [searchValue, setSearchValue] = useState<any>("");
  const [openAddEdit, setOpenAddEdit] = useState<any>(null);

  const devicesList = useMemo(() => Object.values(allBlockedDevices || {}), [allBlockedDevices]);

  const filteredDevices = useMemo(() => devicesList.filter((device: any) => {
    if (!searchValue) {
      return true;
    }
    const serial = device.serial?.toUpperCase() || "";
    const search = searchValue.toUpperCase();
    return serial.indexOf(search) >= 0;
  }), [devicesList, searchValue]);

  const handleDelete = async (id: string) => {
    try {
      await deleteBlockedDevice(id);
    } catch (err: any) {
      addMessage({ message: err?.message || t`Failed to delete blocked device` });
    }
  };

  const handleEdit = (id: string) => {
    setOpenAddEdit(id);
  };

  const handleSave = async (values: any) => {
    try {
      if (openAddEdit === "new") {
        await createBlockedDevice({ serial: values.serial, notes: values.comment });
      } else {
        await editBlockedDevice({ serial: openAddEdit, notes: values.comment });
      }
      setOpenAddEdit(null);
      getAllBlockedDevices();
    } catch (err: any) {
      addMessage({ message: err?.message || t`Failed to save blocked device` });
    }
  };

  return (
    <>
      <ServiceNavigationBar
        title={t`Blocked Devices`}
        searchComponent={
          <Input
            placeholder={t`Search by serial...`}
            value={searchValue}
            onChange={(event: any) => setSearchValue(event.target.value)}
            disableUnderline={true}
            classes={{ root: styles.inputRoot }}
            startAdornment={
              <InputAdornment position="start" className={styles.searchAdorment}>
                <Search className={styles.searchIcon} />
              </InputAdornment>
            }
            endAdornment={
              searchValue && (
                <IconButton
                  onClick={() => setSearchValue("")}
                  className={styles.closeIconStyle}
                >
                  <Close />
                </IconButton>
              )
            }
          />
        }
        {...props}
      >
        <div className={styles.addButtonContainer}>
          <Button
            className={styles.buttonStyle}
            onClick={() => setOpenAddEdit("new")}
          >{t`Add blocked device`}</Button>
        </div>
        {
          <>
            <Paper elevation={0} className={styles.paperPadding}>
              <TableContainer>
                <Table stickyHeader className="" aria-label="customized table">
                  <colgroup>
                    <col style={{ width: "18%" }} />
                    <col style={{ width: "52%" }} />
                    <col style={{ width: "15%" }} />
                    <col style={{ width: "15%" }} />
                  </colgroup>
                  <TableHead className={styles.tableHead}>
                    <TableRow>
                      <TableCell className={styles.headCells}>{t`SERIAL`}</TableCell>
                      <TableCell className={styles.headCells}>{t`COMMENT`}</TableCell>
                      <TableCell align="center" className={styles.headCells}>{t`EDIT`}</TableCell>
                      <TableCell align="center" className={styles.headCells}>{t`DELETE`}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredDevices.map((device: any) => {
                      return (
                        <TableRow hover tabIndex={-1} key={device.serial}>
                          <TableCell
                            component="th"
                            scope="row"
                            align="left"
                            className={styles.rowCell}
                          >
                            {device.serial}
                          </TableCell>
                          <TableCell
                            component="th"
                            scope="row"
                            align="left"
                            className={styles.rowCell}
                          >
                            {device.notes}
                          </TableCell>
                          <TableCell
                            component="th"
                            scope="row"
                            align="center"
                            className={styles.rowCell}
                          >
                            <IconButton onClick={() => handleEdit(device.serial)}>
                              <img src={EditIcon} alt="edit" />
                            </IconButton>
                          </TableCell>
                          <TableCell
                            component="th"
                            scope="row"
                            align="center"
                            className={styles.rowCell}
                          >
                            <IconButton
                              onClick={() => handleDelete(device.serial)}
                            >
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
            {openAddEdit &&
              <AddEditBlockedDevice
                isEdit={openAddEdit !== "new"}
                device={openAddEdit !== "new" ? allBlockedDevices[openAddEdit] : null}
                setOpenAddEdit={setOpenAddEdit}
                handleSave={handleSave}
              />
            }
          </>
        }
      </ServiceNavigationBar>
    </>
  );
};

export default BlockedDevices;
